import { LegalPolicyFooter } from "@/components/legal/LegalPolicyLinks";
import {
  getReturnButtonLabel,
  getSafeReturnTo,
} from "@/lib/navigation/publicReturnTo";
import { getPublicBottomBarPadding } from "@/components/public/PublicBottomBar";
import {
  SOKO_GRID_EMAIL,
  SOKO_GRID_EMAIL_URL,
  SOKO_GRID_PHONE_DISPLAY,
  SOKO_GRID_PHONE_URL,
  SOKO_GRID_WEBSITE_DISPLAY,
  SOKO_GRID_WEBSITE_URL,
  SOKO_GRID_WHATSAPP_DISPLAY,
  SOKO_GRID_WHATSAPP_URL,
} from "@/src/config/contact";
import { router, useLocalSearchParams } from "expo-router";
import React, { useCallback, useMemo } from "react";
import {
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

const PUBLIC_PAGE_BOTTOM_CLEARANCE = 120;

const webScrollHostStyle =
  Platform.OS === "web"
    ? ({
        overflowX: "hidden",
        overflowY: "auto",
      } as any)
    : null;

function goToReturnPath(returnTo: string) {
  if (returnTo) {
    router.push(returnTo as any);
    return;
  }

  router.push("/" as any);
}

async function openEmail() {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.location.href = SOKO_GRID_EMAIL_URL;
    return;
  }

  await Linking.openURL(SOKO_GRID_EMAIL_URL);
}

async function openContactUrl(url: string) {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.location.href = url;
    return;
  }

  await Linking.openURL(url);
}

export default function ContactSokoGridPage() {
  const insets = useSafeAreaInsets();
  const params = useLocalSearchParams<{ returnTo?: string | string[] }>();
  const returnTo = useMemo(
    () => getSafeReturnTo(params.returnTo),
    [params.returnTo]
  );
  const bottomPadding = Math.max(
    getPublicBottomBarPadding(insets.bottom),
    PUBLIC_PAGE_BOTTOM_CLEARANCE
  );

  const handleBack = useCallback(() => {
    goToReturnPath(returnTo);
  }, [returnTo]);

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView
        style={[styles.screen, webScrollHostStyle]}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: bottomPadding },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator
      >
        <View style={styles.container}>
          <Text style={styles.title}>Contact Us</Text>
          <Text style={styles.intro}>
            Need help with Soko Grid? Contact us for support, business listing
            questions, onboarding, or general inquiries.
          </Text>

          <View style={styles.infoCard}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Email</Text>
              <Text style={styles.infoValue}>{SOKO_GRID_EMAIL}</Text>
            </View>

            <View style={styles.divider} />

            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Website</Text>
              <Text style={styles.infoValue}>{SOKO_GRID_WEBSITE_DISPLAY}</Text>
            </View>

            <View style={styles.divider} />

            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Call</Text>
              <Text style={styles.infoValue}>{SOKO_GRID_PHONE_DISPLAY}</Text>
            </View>

            <View style={styles.divider} />

            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>WhatsApp</Text>
              <Text style={styles.infoValue}>{SOKO_GRID_WHATSAPP_DISPLAY}</Text>
            </View>
          </View>

          <View style={styles.actions}>
            <Pressable
              onPress={openEmail}
              style={({ pressed }) => [
                styles.primaryButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text style={styles.primaryButtonText}>Email Us</Text>
            </Pressable>

            <Pressable
              onPress={() => openContactUrl(SOKO_GRID_PHONE_URL)}
              style={({ pressed }) => [
                styles.secondaryButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text style={styles.secondaryButtonText}>Call Us</Text>
            </Pressable>

            <Pressable
              onPress={() => openContactUrl(SOKO_GRID_WHATSAPP_URL)}
              style={({ pressed }) => [
                styles.secondaryButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text style={styles.secondaryButtonText}>WhatsApp Us</Text>
            </Pressable>

            <Pressable
              onPress={() => openContactUrl(SOKO_GRID_WEBSITE_URL)}
              style={({ pressed }) => [
                styles.secondaryButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text style={styles.secondaryButtonText}>Open Website</Text>
            </Pressable>

            <Pressable
              onPress={handleBack}
              style={({ pressed }) => [
                styles.secondaryButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text style={styles.secondaryButtonText}>
                {getReturnButtonLabel(returnTo)}
              </Text>
            </Pressable>
          </View>

          <LegalPolicyFooter returnTo="/contact" style={styles.legalLinks} />
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    minHeight: 0,
    backgroundColor: "#f8fafc",
  },
  screen: {
    flex: 1,
    minHeight: 0,
    backgroundColor: "#f8fafc",
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 16,
    paddingVertical: 22,
  },
  container: {
    width: "100%",
    maxWidth: 760,
    alignSelf: "center",
  },
  title: {
    fontSize: 30,
    lineHeight: 36,
    fontWeight: "900",
    color: "#111827",
  },
  intro: {
    marginTop: 10,
    maxWidth: 620,
    fontSize: 16,
    lineHeight: 24,
    fontWeight: "700",
    color: "#475569",
  },
  infoCard: {
    marginTop: 22,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    padding: 18,
  },
  infoRow: {
    gap: 4,
  },
  infoLabel: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "900",
    color: "#64748b",
    textTransform: "uppercase",
  },
  infoValue: {
    fontSize: 17,
    lineHeight: 24,
    fontWeight: "900",
    color: "#111827",
  },
  divider: {
    height: 1,
    backgroundColor: "#edf2f7",
    marginVertical: 14,
  },
  actions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 20,
  },
  legalLinks: {
    marginTop: 18,
  },
  primaryButton: {
    minHeight: 46,
    borderRadius: 8,
    backgroundColor: "#111827",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 18,
  },
  primaryButtonText: {
    fontSize: 14,
    fontWeight: "900",
    color: "#ffffff",
  },
  secondaryButton: {
    minHeight: 46,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 18,
  },
  secondaryButtonText: {
    fontSize: 14,
    fontWeight: "900",
    color: "#111827",
  },
  pressed: {
    opacity: 0.82,
  },
});
